import type { PlatformAiTenant } from '@avrahami/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { platformAiQuery } from '../api/platform';
import { hasCapability, sessionQuery } from '../api/session';
import { BudgetMeter, LimitMark, Meter } from '../components/AiMeters';
import { Stat } from '../components/Stat';
import { he } from '../i18n/he';
import { budgetState, formatCount, formatPeriodMonth, formatUsdMicros, percentOf } from '../lib/ai';
import { errorMessage } from '../lib/format';
import { Alert, Button, Card, Cell, Loading, PageHeader, Table } from '@avrahami/ui';

function TenantRow({ tenant, canManage }: { tenant: PlatformAiTenant; canManage: boolean }) {
  const { jobs } = tenant;
  const state = budgetState(tenant.spentMicros, tenant.limitMicros);
  const percent = percentOf(tenant.spentMicros, tenant.limitMicros);
  return (
    <tr data-testid="ai-tenant" data-slug={tenant.slug} data-state={state}>
      <Cell>
        <Link
          to="/tenants/$tenantId"
          params={{ tenantId: tenant.tenantId }}
          className="font-medium text-brand-700 hover:underline"
        >
          {tenant.displayName}
        </Link>
      </Cell>
      <Cell className="ltr whitespace-nowrap">
        <span data-testid="ai-tenant-spent">{formatUsdMicros(tenant.spentMicros)}</span>
      </Cell>
      <Cell className="whitespace-nowrap">
        <span className="ltr me-2" data-testid="ai-tenant-limit">
          {formatUsdMicros(tenant.limitMicros)}
        </span>
        <LimitMark isDefault={tenant.limitIsDefault} />
      </Cell>
      <Cell>
        <div className="flex min-w-28 items-center gap-2">
          <Meter
            role="meter"
            label={`${he.ai.usageOf} ${tenant.displayName}`}
            percent={percent}
            state={state}
            className="flex-1"
          />
          <span className="ltr text-xs text-slate-600">{percent}%</span>
        </div>
        {state === 'OK' ? null : (
          <p className="mt-1 text-xs text-slate-700">{he.ai.stateBadge[state]}</p>
        )}
      </Cell>
      <Cell className="ltr text-center">{formatCount(tenant.calls)}</Cell>
      <Cell className="ltr whitespace-nowrap text-center">
        {formatCount(tenant.inputTokens)} / {formatCount(tenant.outputTokens)}
      </Cell>
      <Cell className="ltr whitespace-nowrap text-center">
        <span data-testid="ai-tenant-jobs">
          {jobs.queued} / {jobs.running} / {jobs.succeeded} / {jobs.failed}
        </span>
      </Cell>
      {canManage ? (
        <Cell className="whitespace-nowrap">
          <Link
            to="/tenants/$tenantId/copy"
            params={{ tenantId: tenant.tenantId }}
            aria-label={`${he.aiCopy.linkFor} ${tenant.displayName}`}
            className="font-medium text-brand-700 hover:underline"
            data-testid="ai-tenant-copy-link"
          >
            {he.aiCopy.link}
          </Link>
        </Cell>
      ) : null}
    </tr>
  );
}

/**
 * Cost and volume control of the content engine (spec milestone ד): this month's spend against
 * the platform's limit, and every store's share of it. A store's own limit is set on its page.
 */
export function AiPage() {
  const overview = useQuery(platformAiQuery);
  const session = useQuery(sessionQuery);
  const canManage = session.data !== undefined && hasCapability(session.data, 'platform.ai.manage');
  return (
    <>
      <PageHeader
        title={he.ai.title}
        actions={
          <Button
            tone="secondary"
            disabled={overview.isFetching}
            onClick={() => {
              void overview.refetch();
            }}
          >
            {he.ai.refresh}
          </Button>
        }
      />
      <div className="flex flex-col gap-6">
        <p className="text-sm text-slate-600">{he.ai.intro}</p>
        {overview.isPending ? (
          <Loading />
        ) : overview.isError ? (
          <Alert>{errorMessage(overview.error, { FORBIDDEN: he.ai.viewForbidden })}</Alert>
        ) : (
          <>
            <Card title={he.ai.summary}>
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Stat label={he.ai.month} value={formatPeriodMonth(overview.data.periodStart)} />
                  <Stat label={he.ai.spent} value={formatUsdMicros(overview.data.spentMicros)} />
                  <Stat
                    label={he.ai.platformLimit}
                    value={formatUsdMicros(overview.data.limitMicros)}
                  />
                  <Stat
                    label={he.ai.defaultTenantLimit}
                    value={formatUsdMicros(overview.data.defaultTenantLimitMicros)}
                  />
                </div>
                <BudgetMeter
                  spentMicros={overview.data.spentMicros}
                  limitMicros={overview.data.limitMicros}
                  label={he.ai.platformMeter}
                  words={he.ai.platformState}
                />
              </div>
            </Card>
            <Card title={he.ai.tenants}>
              {overview.data.tenants.length === 0 ? (
                <p className="text-sm text-slate-500">{he.ai.empty}</p>
              ) : (
                <Table
                  head={[
                    he.ai.store,
                    he.ai.spent,
                    he.ai.limit,
                    he.ai.usage,
                    he.ai.calls,
                    he.ai.tokens,
                    he.ai.jobs,
                    ...(canManage ? [he.aiCopy.title] : []),
                  ]}
                >
                  {overview.data.tenants.map((tenant) => (
                    <TenantRow key={tenant.tenantId} tenant={tenant} canManage={canManage} />
                  ))}
                </Table>
              )}
            </Card>
          </>
        )}
      </div>
    </>
  );
}
